import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { money, money0, pct, time } from '../lib/format';
import { PageHeader } from '../components/Layout';
import { InvoiceDoc, type Invoice } from '../components/InvoiceDoc';
import { Empty, ErrorState, Loading, Modal, Select, TextInput, useToast } from '../components/ui';

interface MenuItem {
  id: string;
  sku: string;
  name: string;
  category: string;
  price: number;
  gstRate: number;
  foodCostPct: number;
  isVegetarian: boolean;
  isActive: boolean;
}

interface TicketLine {
  id: string;
  menuItemId: string;
  name: string;
  quantity: number;
  unitPrice: number;
  gstRate: number;
  amount: number;
  kdsState: string;
}

interface Ticket {
  id: string;
  number: string;
  type: string;
  status: string;
  tableCode: string | null;
  roomNumber: string | null;
  guestName: string | null;
  stayId: string | null;
  openedAt: string;
  itemCount: number;
  lines: TicketLine[];
  totals: { subtotal: number; cgst: number; sgst: number; igst: number; tax: number; roundOff: number; total: number };
}

interface TableRow {
  id: string;
  code: string;
  seats: number;
  section: string;
  isOccupied: boolean;
}

interface StayRow {
  id: string;
  roomNumber: string;
  guestName: string;
}

const PAYMENT_MODES = ['CASH', 'CARD', 'UPI', 'ROOM'] as const;

export function PosPage() {
  const queryClient = useQueryClient();
  const { push, fail } = useToast();
  const { can } = useAuth();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [discount, setDiscount] = useState(0);
  const [mode, setMode] = useState<(typeof PAYMENT_MODES)[number]>('CASH');
  const [newTicket, setNewTicket] = useState(false);
  const [voidTicket, setVoidTicket] = useState(false);
  const [receipt, setReceipt] = useState<Invoice | null>(null);

  const menu = useQuery({ queryKey: ['menu'], queryFn: () => api.get<MenuItem[]>('/api/menu?activeOnly=true') });
  const tickets = useQuery({
    queryKey: ['tickets', 'OPEN'],
    queryFn: () => api.get<Ticket[]>('/api/tickets?status=OPEN'),
    refetchInterval: 20_000,
  });

  const activeTicketId = activeId ?? tickets.data?.[0]?.id ?? null;
  const ticket = useQuery({
    queryKey: ['ticket', activeTicketId],
    queryFn: () => api.get<Ticket>(`/api/tickets/${activeTicketId}`),
    enabled: Boolean(activeTicketId),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    void queryClient.invalidateQueries({ queryKey: ['ticket', activeTicketId] });
  };

  const addLine = useMutation({
    mutationFn: (input: { menuItemId: string; quantity: number }) =>
      api.post<Ticket>(`/api/tickets/${activeTicketId}/lines`, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['ticket', activeTicketId], data);
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: fail,
  });

  const removeLine = useMutation({
    mutationFn: (lineId: string) => api.delete<Ticket>(`/api/tickets/${activeTicketId}/lines/${lineId}`),
    onSuccess: (data) => {
      queryClient.setQueryData(['ticket', activeTicketId], data);
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: fail,
  });

  const bill = useMutation({
    mutationFn: () =>
      api.post<Invoice>(`/api/tickets/${activeTicketId}/bill`, {
        mode,
        discount,
        discountReason: discount > 0 ? 'Counter discount' : '',
      }),
    onSuccess: (invoice) => {
      push(`${invoice.number} settled · ${money0(invoice.total)}`, 'ok');
      setReceipt(invoice);
      setDiscount(0);
      setActiveId(null);
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: fail,
  });

  const voidMutation = useMutation({
    mutationFn: (reason: string) => api.post(`/api/tickets/${activeTicketId}/void`, { reason }),
    onSuccess: () => {
      push('Ticket voided');
      setVoidTicket(false);
      setActiveId(null);
      invalidate();
    },
    onError: fail,
  });

  const categories = useMemo(
    () => ['All', ...new Set((menu.data ?? []).map((item) => item.category))],
    [menu.data],
  );

  const visibleItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (menu.data ?? []).filter(
      (item) =>
        (category === 'All' || item.category === category) &&
        (term === '' || item.name.toLowerCase().includes(term) || item.sku.toLowerCase().includes(term)),
    );
  }, [menu.data, category, search]);

  const current = ticket.data;
  const netAfterDiscount = Math.max(0, (current?.totals.subtotal ?? 0) - discount);
  const taxFactor = current && current.totals.subtotal > 0 ? netAfterDiscount / current.totals.subtotal : 0;
  const estimatedTax = (current?.totals.tax ?? 0) * taxFactor;
  const estimatedTotal = Math.round(netAfterDiscount + estimatedTax);

  if (menu.isLoading || tickets.isLoading) return <Loading label="Loading the counter…" />;
  if (menu.error) return <ErrorState error={menu.error} onRetry={() => void menu.refetch()} />;

  return (
    <div className="stack">
      <PageHeader
        title="POS Billing"
        subtitle="Ticket → GST invoice → automatic stock depletion"
        actions={
          <button className="btn primary" onClick={() => setNewTicket(true)}>
            + New ticket
          </button>
        }
      />

      <div className="row between wrap gap-6">
        <div className="ticket-strip">
          {(tickets.data ?? []).length === 0 ? (
            <div className="chip">No open tickets — start one from the top right</div>
          ) : (
            tickets.data!.map((row) => (
              <button
                key={row.id}
                className={`ticket-tab ${row.id === activeTicketId ? 'active' : ''}`}
                onClick={() => {
                  setActiveId(row.id);
                  setDiscount(0);
                  setMode(row.stayId ? 'ROOM' : 'CASH');
                }}
              >
                <div className="tt-no">{row.number}</div>
                <div className="tt-meta">
                  {row.tableCode
                    ? `Table ${row.tableCode}`
                    : row.roomNumber
                      ? `Room ${row.roomNumber}`
                      : row.type.replace('_', ' ')}{' '}
                  · {money0(row.totals.subtotal)}
                </div>
              </button>
            ))
          )}
        </div>
        <span className="chip">{tickets.data?.length ?? 0} open</span>
      </div>

      <div className="pos">
        <div className="card">
          <div className="card-head">
            <div className="tabs">
              {categories.map((name) => (
                <button
                  key={name}
                  className={`tab ${name === category ? 'active' : ''}`}
                  onClick={() => setCategory(name)}
                >
                  {name}
                </button>
              ))}
            </div>
            <div className="search" style={{ width: 210 }}>
              <input
                className="input"
                placeholder="Search item or SKU"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="card-body">
            {visibleItems.length === 0 ? (
              <Empty message="No items match that search" />
            ) : (
              <div className="menu-grid">
                {visibleItems.map((item) => (
                  <button
                    key={item.id}
                    className="menu-card"
                    disabled={!activeTicketId || addLine.isPending}
                    onClick={() => addLine.mutate({ menuItemId: item.id, quantity: 1 })}
                  >
                    <div className="row gap-6">
                      <span className={`veg-dot ${item.isVegetarian ? '' : 'nonveg'}`} />
                      <span className="xs muted mono">{item.sku}</span>
                    </div>
                    <div className="mc-name">{item.name}</div>
                    <div className="row between mc-price">
                      <span>{money0(item.price)}</span>
                      <span className="xs muted">{pct(item.foodCostPct)} FC</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="card ticket">
          {!current ? (
            <Empty icon="▤" message="Select or start a ticket" />
          ) : (
            <>
              <div className="card-head">
                <div>
                  <h2>{current.number}</h2>
                  <div className="small muted">
                    {current.tableCode
                      ? `Table ${current.tableCode}`
                      : current.roomNumber
                        ? `Room ${current.roomNumber} · ${current.guestName}`
                        : current.type.replace('_', ' ')}{' '}
                    · {time(current.openedAt)}
                  </div>
                </div>
                {can('pos:void') ? (
                  <button className="btn ghost icon sm" title="Void ticket" onClick={() => setVoidTicket(true)}>
                    ✕
                  </button>
                ) : null}
              </div>

              <div className="ticket-lines">
                {current.lines.length === 0 ? (
                  <Empty message="Tap items to add them" />
                ) : (
                  current.lines.map((line) => (
                    <div className="tline" key={line.id}>
                      <div className="tl-name">{line.name}</div>
                      <div className="tl-amt">{money(line.amount)}</div>
                      <div className="tl-meta">
                        <div className="stepper">
                          <button
                            onClick={() => addLine.mutate({ menuItemId: line.menuItemId, quantity: -1 })}
                            disabled={addLine.isPending}
                          >
                            −
                          </button>
                          <span className="q">{line.quantity}</span>
                          <button
                            onClick={() => addLine.mutate({ menuItemId: line.menuItemId, quantity: 1 })}
                            disabled={addLine.isPending}
                          >
                            +
                          </button>
                        </div>
                        <span className="xs muted">
                          @ {money0(line.unitPrice)} · GST {line.gstRate}%
                        </span>
                        <button
                          className="btn ghost sm"
                          style={{ marginLeft: 'auto' }}
                          onClick={() => removeLine.mutate(line.id)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="totals">
                <div className="t-row">
                  <span>Taxable value</span>
                  <span className="num">{money(current.totals.subtotal)}</span>
                </div>
                <div className="t-row row between" style={{ alignItems: 'center' }}>
                  <span>Discount</span>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step={10}
                    value={discount}
                    disabled={!can('pos:discount')}
                    title={can('pos:discount') ? '' : 'Your role cannot apply discounts'}
                    onChange={(e) => setDiscount(Math.max(0, Number(e.target.value) || 0))}
                    style={{ width: 96, height: 27, textAlign: 'right' }}
                  />
                </div>
                <div className="t-row">
                  <span>GST</span>
                  <span className="num">{money(estimatedTax)}</span>
                </div>
                <div className="t-row">
                  <span>Round off</span>
                  <span className="num">{money(estimatedTotal - (netAfterDiscount + estimatedTax))}</span>
                </div>
                <div className="t-row grand">
                  <span>Total</span>
                  <span className="num">{money(estimatedTotal)}</span>
                </div>

                <div className="pay-modes" style={{ marginTop: 11 }}>
                  {PAYMENT_MODES.map((option) => (
                    <button
                      key={option}
                      className={mode === option ? 'active' : ''}
                      disabled={option === 'ROOM' && !current.stayId}
                      onClick={() => setMode(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>

                <button
                  className="btn primary lg block"
                  style={{ marginTop: 10 }}
                  disabled={current.lines.length === 0 || bill.isPending}
                  onClick={() => bill.mutate()}
                >
                  {bill.isPending ? 'Billing…' : `Bill ${money0(estimatedTotal)}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {newTicket ? <NewTicketModal onClose={() => setNewTicket(false)} onCreated={(id) => setActiveId(id)} /> : null}

      {voidTicket && current ? (
        <VoidTicketDialog
          number={current.number}
          busy={voidMutation.isPending}
          onCancel={() => setVoidTicket(false)}
          onConfirm={(reason) => voidMutation.mutate(reason)}
        />
      ) : null}

      {receipt ? (
        <Modal
          title={`Invoice ${receipt.number}`}
          size="wide"
          onClose={() => setReceipt(null)}
          footer={
            <>
              <button className="btn" onClick={() => setReceipt(null)}>
                Close
              </button>
              <button className="btn primary" onClick={() => window.print()}>
                Print
              </button>
            </>
          }
        >
          <InvoiceDoc invoice={receipt} />
        </Modal>
      ) : null}
    </div>
  );
}

function VoidTicketDialog({
  number,
  busy,
  onCancel,
  onConfirm,
}: {
  number: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      title={`Void ${number}`}
      size="narrow"
      onClose={onCancel}
      footer={
        <>
          <button className="btn" onClick={onCancel}>
            Keep ticket
          </button>
          <button className="btn danger" disabled={!reason.trim() || busy} onClick={() => onConfirm(reason.trim())}>
            Void ticket
          </button>
        </>
      }
    >
      <p className="dim" style={{ marginBottom: 14 }}>
        Nothing will be billed. The reason is written to the audit log.
      </p>
      <TextInput
        label="Reason"
        autoFocus
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. guest left, keyed on the wrong table"
      />
    </Modal>
  );
}

function NewTicketModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const queryClient = useQueryClient();
  const { fail } = useToast();
  const [type, setType] = useState('DINE_IN');
  const [tableId, setTableId] = useState('');
  const [stayId, setStayId] = useState('');
  const [covers, setCovers] = useState(2);

  const tables = useQuery({ queryKey: ['tables'], queryFn: () => api.get<TableRow[]>('/api/tables') });
  const stays = useQuery({ queryKey: ['stays', 'IN_HOUSE'], queryFn: () => api.get<StayRow[]>('/api/stays') });

  const create = useMutation({
    mutationFn: () =>
      api.post<Ticket>('/api/tickets', {
        type,
        covers,
        ...(type === 'DINE_IN' ? { tableId } : {}),
        ...(type === 'ROOM_SERVICE' ? { stayId } : {}),
      }),
    onSuccess: (ticket) => {
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      onCreated(ticket.id);
      onClose();
    },
    onError: fail,
  });

  const free = (tables.data ?? []).filter((table) => !table.isOccupied);

  return (
    <Modal
      title="New ticket"
      size="narrow"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={create.isPending || (type === 'DINE_IN' && !tableId) || (type === 'ROOM_SERVICE' && !stayId)}
            onClick={() => create.mutate()}
          >
            Open ticket
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <Select label="Service type" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="DINE_IN">Dine-in</option>
          <option value="TAKEAWAY">Takeaway</option>
          <option value="DELIVERY">Delivery</option>
          <option value="ROOM_SERVICE" disabled={(stays.data?.length ?? 0) === 0}>
            Room service
          </option>
        </Select>

        {type === 'DINE_IN' ? (
          <Select
            label="Table"
            value={tableId}
            onChange={(e) => setTableId(e.target.value)}
            hint={free.length === 0 ? 'Every table has an open ticket' : undefined}
          >
            <option value="">Select a table…</option>
            {free.map((table) => (
              <option key={table.id} value={table.id}>
                {table.code} · {table.seats} seats · {table.section}
              </option>
            ))}
          </Select>
        ) : null}

        {type === 'ROOM_SERVICE' ? (
          <Select label="In-house guest" value={stayId} onChange={(e) => setStayId(e.target.value)}>
            <option value="">Select a room…</option>
            {(stays.data ?? []).map((stay) => (
              <option key={stay.id} value={stay.id}>
                Room {stay.roomNumber} · {stay.guestName}
              </option>
            ))}
          </Select>
        ) : null}

        <TextInput
          label="Covers"
          type="number"
          min={1}
          max={50}
          value={covers}
          onChange={(e) => setCovers(Number(e.target.value) || 1)}
        />
      </div>
    </Modal>
  );
}
